/** Highway assets stored as LRS events (point or linear / dynamic segmentation). */

export type LrsAssetGeometryKind = 'point' | 'linear';

export interface LrsAssetTypeMeta {
  id: string;
  label: string;
  color: string;
  /** @deprecated Use iconPath — SVG in assets/icons/lrs */
  icon: string;
  iconPath: string;
  /** Style-engine reference (PDF §3 — not stored as image in DB) */
  symbolCode: string;
  geometryKind: LrsAssetGeometryKind;
  /** Default span in metres for linear assets when placing at chainage */
  defaultSpanM?: number;
  /** Point assets eligible for OpenLayers cluster source */
  clusterable?: boolean;
}

export const LRS_ASSET_EVENT_TYPES: readonly LrsAssetTypeMeta[] = [
  {
    id: 'sign',
    label: 'Sign',
    color: '#2563eb',
    icon: '🪧',
    iconPath: 'assets/icons/lrs/sign.svg',
    symbolCode: 'sign',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'culvert',
    label: 'Culvert',
    color: '#0891b2',
    icon: '〰',
    iconPath: 'assets/icons/lrs/culvert.svg',
    symbolCode: 'culvert',
    geometryKind: 'linear',
    defaultSpanM: 30,
    clusterable: false,
  },
  {
    id: 'bridge',
    label: 'Bridge',
    color: '#7c3aed',
    icon: '🌉',
    iconPath: 'assets/icons/lrs/bridge.svg',
    symbolCode: 'bridge',
    geometryKind: 'linear',
    defaultSpanM: 120,
    clusterable: false,
  },
  {
    id: 'crash_barrier',
    label: 'Crash Barrier',
    color: '#dc2626',
    icon: '🛡',
    iconPath: 'assets/icons/lrs/crash_barrier.svg',
    symbolCode: 'crash_barrier',
    geometryKind: 'linear',
    defaultSpanM: 500,
    clusterable: false,
  },
  {
    id: 'lighting',
    label: 'Lighting',
    color: '#ca8a04',
    icon: '💡',
    iconPath: 'assets/icons/lrs/lighting.svg',
    symbolCode: 'lighting',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'cctv',
    label: 'CCTV',
    color: '#7c3aed',
    icon: '📹',
    iconPath: 'assets/icons/lrs/cctv.svg',
    symbolCode: 'cctv',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'vms',
    label: 'VMS',
    color: '#9333ea',
    icon: '📺',
    iconPath: 'assets/icons/lrs/vms.svg',
    symbolCode: 'vms',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'atms',
    label: 'ATMS',
    color: '#6366f1',
    icon: '🛰',
    iconPath: 'assets/icons/lrs/atms.svg',
    symbolCode: 'atms',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'sos',
    label: 'SOS',
    color: '#ef4444',
    icon: '🆘',
    iconPath: 'assets/icons/lrs/sos.svg',
    symbolCode: 'sos',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'toll_plaza',
    label: 'Toll plaza',
    color: '#0f766e',
    icon: '🛂',
    iconPath: 'assets/icons/lrs/toll_plaza.svg',
    symbolCode: 'toll_plaza',
    geometryKind: 'linear',
    defaultSpanM: 80,
    clusterable: false,
  },
  {
    id: 'bus_bay',
    label: 'Bus bay',
    color: '#2563eb',
    icon: '🚌',
    iconPath: 'assets/icons/lrs/bus_bay.svg',
    symbolCode: 'bus_bay',
    geometryKind: 'linear',
    defaultSpanM: 40,
    clusterable: false,
  },
  {
    id: 'truck_bay',
    label: 'Truck bay',
    color: '#b45309',
    icon: '🚛',
    iconPath: 'assets/icons/lrs/truck_bay.svg',
    symbolCode: 'truck_bay',
    geometryKind: 'linear',
    defaultSpanM: 50,
    clusterable: false,
  },
  {
    id: 'wim',
    label: 'WIM station',
    color: '#7c3aed',
    icon: '⚖',
    iconPath: 'assets/icons/lrs/wim.svg',
    symbolCode: 'wim',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'breakdown_parking',
    label: 'Breakdown parking',
    color: '#475569',
    icon: '🅿',
    iconPath: 'assets/icons/lrs/breakdown_parking.svg',
    symbolCode: 'breakdown_parking',
    geometryKind: 'linear',
    defaultSpanM: 60,
    clusterable: false,
  },
  {
    id: 'crossover',
    label: 'Crossover',
    color: '#c026d3',
    icon: '✕',
    iconPath: 'assets/icons/lrs/crossover.svg',
    symbolCode: 'crossover',
    geometryKind: 'linear',
    defaultSpanM: 40,
    clusterable: false,
  },
  {
    id: 'underpass',
    label: 'Underpass',
    color: '#0f766e',
    icon: '⊓',
    iconPath: 'assets/icons/lrs/underpass.svg',
    symbolCode: 'underpass',
    geometryKind: 'linear',
    defaultSpanM: 50,
    clusterable: false,
  },
  {
    id: 'overpass',
    label: 'Overpass',
    color: '#a21caf',
    icon: '∩',
    iconPath: 'assets/icons/lrs/overpass.svg',
    symbolCode: 'overpass',
    geometryKind: 'linear',
    defaultSpanM: 80,
    clusterable: false,
  },
  {
    id: 'km_stone',
    label: 'Kilometre stone',
    color: '#1d4ed8',
    icon: '📍',
    iconPath: 'assets/icons/lrs/sign.svg',
    symbolCode: 'km_stone',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'chainage_marker',
    label: 'Chainage marker',
    color: '#2563eb',
    icon: '📌',
    iconPath: 'assets/icons/lrs/sign.svg',
    symbolCode: 'chainage_marker',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'guard_rail',
    label: 'Guard rail',
    color: '#b91c1c',
    icon: '🛡',
    iconPath: 'assets/icons/lrs/crash_barrier.svg',
    symbolCode: 'guard_rail',
    geometryKind: 'linear',
    defaultSpanM: 200,
    clusterable: false,
  },
  {
    id: 'delineator',
    label: 'Delineator',
    color: '#ea580c',
    icon: '▮',
    iconPath: 'assets/icons/lrs/sign.svg',
    symbolCode: 'delineator',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'road_stud',
    label: 'Road stud',
    color: '#f59e0b',
    icon: '✦',
    iconPath: 'assets/icons/lrs/lighting.svg',
    symbolCode: 'road_stud',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'gantry',
    label: 'Gantry',
    color: '#7c3aed',
    icon: '⊓',
    iconPath: 'assets/icons/lrs/vms.svg',
    symbolCode: 'gantry',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'sign_board',
    label: 'Sign board',
    color: '#1d4ed8',
    icon: '🪧',
    iconPath: 'assets/icons/lrs/sign.svg',
    symbolCode: 'sign_board',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'bus_shelter',
    label: 'Bus shelter',
    color: '#2563eb',
    icon: '🏠',
    iconPath: 'assets/icons/lrs/bus_bay.svg',
    symbolCode: 'bus_shelter',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'bollard',
    label: 'Bollard',
    color: '#334155',
    icon: '●',
    iconPath: 'assets/icons/lrs/sign.svg',
    symbolCode: 'bollard',
    geometryKind: 'point',
    clusterable: true,
  },
  {
    id: 'rob',
    label: 'ROB',
    color: '#6d28d9',
    icon: '🌉',
    iconPath: 'assets/icons/lrs/bridge.svg',
    symbolCode: 'rob',
    geometryKind: 'linear',
    defaultSpanM: 80,
    clusterable: false,
  },
  {
    id: 'rub',
    label: 'RUB',
    color: '#0f766e',
    icon: '⊓',
    iconPath: 'assets/icons/lrs/underpass.svg',
    symbolCode: 'rub',
    geometryKind: 'linear',
    defaultSpanM: 50,
    clusterable: false,
  },
  {
    id: 'flyover',
    label: 'Flyover',
    color: '#7c3aed',
    icon: '∩',
    iconPath: 'assets/icons/lrs/overpass.svg',
    symbolCode: 'flyover',
    geometryKind: 'linear',
    defaultSpanM: 200,
    clusterable: false,
  },
  {
    id: 'viaduct',
    label: 'Viaduct',
    color: '#5b21b6',
    icon: '🌉',
    iconPath: 'assets/icons/lrs/bridge.svg',
    symbolCode: 'viaduct',
    geometryKind: 'linear',
    defaultSpanM: 250,
    clusterable: false,
  },
  {
    id: 'box_culvert',
    label: 'Box culvert',
    color: '#0e7490',
    icon: '▢',
    iconPath: 'assets/icons/lrs/culvert.svg',
    symbolCode: 'box_culvert',
    geometryKind: 'linear',
    defaultSpanM: 20,
    clusterable: false,
  },
  {
    id: 'retaining_wall',
    label: 'Retaining wall',
    color: '#57534e',
    icon: '▬',
    iconPath: 'assets/icons/lrs/crash_barrier.svg',
    symbolCode: 'retaining_wall',
    geometryKind: 'linear',
    defaultSpanM: 80,
    clusterable: false,
  },
  {
    id: 'subway',
    label: 'Subway',
    color: '#115e59',
    icon: '⊓',
    iconPath: 'assets/icons/lrs/underpass.svg',
    symbolCode: 'subway',
    geometryKind: 'linear',
    defaultSpanM: 40,
    clusterable: false,
  },
  {
    id: 'shoulder',
    label: 'Shoulder',
    color: '#64748b',
    icon: '▬',
    iconPath: 'assets/icons/lrs/pavement.svg',
    symbolCode: 'shoulder',
    geometryKind: 'linear',
    defaultSpanM: 500,
    clusterable: false,
  },
  {
    id: 'median',
    label: 'Median',
    color: '#15803d',
    icon: '▬',
    iconPath: 'assets/icons/lrs/cross_section.svg',
    symbolCode: 'median',
    geometryKind: 'linear',
    defaultSpanM: 500,
    clusterable: false,
  },
  {
    id: 'camber',
    label: 'Camber',
    color: '#7e22ce',
    icon: '∠',
    iconPath: 'assets/icons/lrs/superelevation.svg',
    symbolCode: 'camber',
    geometryKind: 'linear',
    defaultSpanM: 200,
    clusterable: false,
  },
] as const;

/** Linear attribute events (PDF road_events examples) */
export const LRS_LINEAR_EVENT_TYPES: readonly { id: string; label: string; color: string }[] = [
  { id: 'geometry', label: 'Geometry', color: '#64748b' },
  { id: 'pavement', label: 'Pavement', color: '#475569' },
  { id: 'cross_section', label: 'Cross Section', color: '#0d9488' },
  { id: 'superelevation', label: 'Superelevation', color: '#9333ea' },
] as const;

export type LrsAssetEventType = (typeof LRS_ASSET_EVENT_TYPES)[number]['id'];

const ASSET_TYPE_IDS = new Set(LRS_ASSET_EVENT_TYPES.map((t) => t.id));

export function isLrsAssetEventType(eventType: string | null | undefined): boolean {
  return !!eventType && ASSET_TYPE_IDS.has(eventType);
}

export function getLrsAssetTypeMeta(eventType: string | null | undefined): LrsAssetTypeMeta | undefined {
  return LRS_ASSET_EVENT_TYPES.find((t) => t.id === eventType);
}

export function defaultAssetLabel(eventType: string, measureKm: number, measureToKm?: number): string {
  const meta = getLrsAssetTypeMeta(eventType);
  const name = meta?.label || eventType;
  if (measureToKm != null && measureToKm > measureKm) {
    return `${name} ${measureKm.toFixed(3)}–${measureToKm.toFixed(3)} km`;
  }
  return `${name} @ ${measureKm.toFixed(3)} km`;
}

export function isLinearAssetType(eventType: string): boolean {
  const meta = getLrsAssetTypeMeta(eventType);
  if (meta) return meta.geometryKind === 'linear';
  return LRS_LINEAR_EVENT_TYPES.some((t) => t.id === eventType);
}

export const LRS_UTILITY_TYPES: readonly {
  id: string;
  label: string;
  color: string;
  iconPath: string;
  symbolCode: string;
  geometryKind?: LrsAssetGeometryKind;
}[] = [
  { id: 'water', label: 'Water', color: '#0284c7', iconPath: 'assets/icons/lrs/water.svg', symbolCode: 'water' },
  { id: 'sewer', label: 'Sewer', color: '#57534e', iconPath: 'assets/icons/lrs/sewer.svg', symbolCode: 'sewer' },
  {
    id: 'storm_drain',
    label: 'Storm drain',
    color: '#0369a1',
    iconPath: 'assets/icons/lrs/storm_drain.svg',
    symbolCode: 'storm_drain',
  },
  { id: 'ofc', label: 'OFC / Telecom', color: '#ea580c', iconPath: 'assets/icons/lrs/ofc.svg', symbolCode: 'ofc' },
  {
    id: 'electric_ht',
    label: 'Electric HT',
    color: '#eab308',
    iconPath: 'assets/icons/lrs/electric_ht.svg',
    symbolCode: 'electric_ht',
  },
  {
    id: 'electric_lt',
    label: 'Electric LT',
    color: '#facc15',
    iconPath: 'assets/icons/lrs/electric_lt.svg',
    symbolCode: 'electric_lt',
  },
  { id: 'oil_gas', label: 'Oil / Gas', color: '#b45309', iconPath: 'assets/icons/lrs/oil_gas.svg', symbolCode: 'oil_gas' },
  {
    id: 'drainage',
    label: 'Drainage',
    color: '#14b8a6',
    iconPath: 'assets/icons/lrs/drainage.svg',
    symbolCode: 'drainage',
  },
  {
    id: 'chamber',
    label: 'Chamber',
    color: '#0f766e',
    iconPath: 'assets/icons/lrs/drainage.svg',
    symbolCode: 'chamber',
    geometryKind: 'point',
  },
  {
    id: 'manhole',
    label: 'Manhole',
    color: '#44403c',
    iconPath: 'assets/icons/lrs/drainage.svg',
    symbolCode: 'manhole',
    geometryKind: 'point',
  },
  {
    id: 'valve_chamber',
    label: 'Valve chamber',
    color: '#0369a1',
    iconPath: 'assets/icons/lrs/water.svg',
    symbolCode: 'valve_chamber',
    geometryKind: 'point',
  },
  {
    id: 'inspection_chamber',
    label: 'Inspection chamber',
    color: '#155e75',
    iconPath: 'assets/icons/lrs/drainage.svg',
    symbolCode: 'inspection_chamber',
    geometryKind: 'point',
  },
  {
    id: 'utility_duct',
    label: 'Utility duct',
    color: '#c2410c',
    iconPath: 'assets/icons/lrs/ofc.svg',
    symbolCode: 'utility_duct',
  },
  {
    id: 'electric_pole',
    label: 'Electricity pole',
    color: '#ca8a04',
    iconPath: 'assets/icons/lrs/electric_lt.svg',
    symbolCode: 'electric_pole',
    geometryKind: 'point',
  },
  {
    id: 'electric_tower',
    label: 'Electricity tower',
    color: '#a16207',
    iconPath: 'assets/icons/lrs/electric_ht.svg',
    symbolCode: 'electric_tower',
    geometryKind: 'point',
  },
  {
    id: 'telecom_tower',
    label: 'Telecom tower',
    color: '#ea580c',
    iconPath: 'assets/icons/lrs/ofc.svg',
    symbolCode: 'telecom_tower',
    geometryKind: 'point',
  },
  {
    id: 'oh_water_tank',
    label: 'O/H water tank',
    color: '#0284c7',
    iconPath: 'assets/icons/lrs/water.svg',
    symbolCode: 'oh_water_tank',
    geometryKind: 'point',
  },
] as const;

export type LrsWorkbenchPanel =
  | 'assets'
  | 'attributes'
  | 'survey'
  | 'maintenance'
  | 'utilities'
  | 'corridor'
  | 'network'
  | 'unm'
  | 'route'
  | 'clash'
  | 'reports'
  | 'field'
  | 'alignment';

export const LRS_EVENT_BEHAVIORS = [
  { id: 'default', label: 'Default' },
  { id: 'cover', label: 'Cover (stretch on route extend)' },
  { id: 'shift', label: 'Shift measures' },
  { id: 'slice', label: 'Time slice (new version)' },
  { id: 'retire', label: 'Retire assets outside range (shrink)' },
] as const;

export const LRS_ROUTE_LENGTH_MODES = [
  { id: 'increase', label: 'Increase length' },
  { id: 'decrease', label: 'Decrease length' },
] as const;

export const LRS_UTILITY_DEPTH_RISK: Record<string, 'critical' | 'high' | 'medium' | 'low'> = {
  electric_ht: 'critical',
  oil_gas: 'high',
  ofc: 'medium',
  water: 'low',
  sewer: 'low',
  storm_drain: 'low',
  electric_lt: 'medium',
  drainage: 'low',
  utility_duct: 'medium',
  chamber: 'low',
  manhole: 'low',
  valve_chamber: 'low',
  inspection_chamber: 'low',
};

export type LrsGrade = 'underground' | 'overground' | 'crossover' | 'at_grade';

export type LrsLifecycleStatus =
  | 'planned'
  | 'design'
  | 'construction'
  | 'operational'
  | 'maintenance'
  | 'decommissioned';

export type LrsCondition = 'excellent' | 'good' | 'fair' | 'poor' | 'critical';

export const LRS_LIFECYCLE_STAGES: readonly { id: LrsLifecycleStatus; label: string }[] = [
  { id: 'planned', label: 'Planned' },
  { id: 'design', label: 'Design' },
  { id: 'construction', label: 'Construction' },
  { id: 'operational', label: 'Operational' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'decommissioned', label: 'Decommissioned' },
];

export const LRS_CONDITIONS: readonly { id: LrsCondition; label: string }[] = [
  { id: 'excellent', label: 'Excellent' },
  { id: 'good', label: 'Good' },
  { id: 'fair', label: 'Fair' },
  { id: 'poor', label: 'Poor' },
  { id: 'critical', label: 'Critical' },
];

export const LRS_GRADES: readonly { id: LrsGrade; label: string }[] = [
  { id: 'overground', label: 'Overground' },
  { id: 'underground', label: 'Underground' },
  { id: 'crossover', label: 'Crossover' },
  { id: 'at_grade', label: 'At grade' },
] as const;

const CROSSOVER_EVENT_TYPES = new Set([
  'crossover',
  'underpass',
  'overpass',
  'culvert',
  'bridge',
  'rob',
  'rub',
  'flyover',
  'viaduct',
  'box_culvert',
]);

const AT_GRADE_EVENT_TYPES = new Set([
  'pavement',
  'shoulder',
  'median',
  'camber',
  'crash_barrier',
  'guard_rail',
]);

const UG_UTILITY_TYPES = new Set([
  'water',
  'sewer',
  'storm_drain',
  'oil_gas',
  'drainage',
  'chamber',
  'manhole',
  'valve_chamber',
  'inspection_chamber',
  'utility_duct',
]);

const OG_UTILITY_TYPES = new Set([
  'electric_pole',
  'electric_tower',
  'telecom_tower',
  'oh_water_tank',
]);

export function getUtilityTypeMeta(utilityType: string | null | undefined) {
  return LRS_UTILITY_TYPES.find((t) => t.id === utilityType);
}

export function normalizeLrsGrade(value: unknown): LrsGrade | null {
  const g = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[-\s]+/g, '_');
  if (g === 'atgrade') return 'at_grade';
  if (g === 'buried' || g === 'ug') return 'underground';
  if (g === 'overhead' || g === 'aerial' || g === 'og') return 'overground';
  if (g === 'crossing' || g === 'grade_separated') return 'crossover';
  if (g === 'underground' || g === 'overground' || g === 'crossover' || g === 'at_grade') return g;
  return null;
}

export function inferLrsGrade(input: {
  grade?: unknown;
  classification?: unknown;
  depth_m?: number | null;
  event_type?: string | null;
  properties?: Record<string, unknown> | null;
}): LrsGrade {
  const explicit =
    normalizeLrsGrade(input.grade) ||
    normalizeLrsGrade(input.classification) ||
    normalizeLrsGrade(input.properties?.['grade']) ||
    normalizeLrsGrade(input.properties?.['classification']);
  if (explicit) return explicit;
  if (input.event_type && CROSSOVER_EVENT_TYPES.has(input.event_type)) return 'crossover';
  if (input.event_type && AT_GRADE_EVENT_TYPES.has(input.event_type)) return 'at_grade';
  const depth = input.depth_m != null ? Math.abs(Number(input.depth_m)) : 0;
  if (Number.isFinite(depth) && depth > 0) return 'underground';
  return 'overground';
}

export function defaultGradeForEventType(eventType: string): LrsGrade {
  if (CROSSOVER_EVENT_TYPES.has(eventType)) return 'crossover';
  if (AT_GRADE_EVENT_TYPES.has(eventType)) return 'at_grade';
  return 'overground';
}

export function defaultGradeForUtilityType(utilityType: string): LrsGrade {
  if (UG_UTILITY_TYPES.has(utilityType)) return 'underground';
  if (OG_UTILITY_TYPES.has(utilityType)) return 'overground';
  return 'underground';
}

export type LrsSpecFieldKind = 'text' | 'number' | 'date' | 'select' | 'readonly';
export type LrsSpecStore = 'column' | 'properties';

export interface LrsSpecField {
  key: string;
  label: string;
  kind: LrsSpecFieldKind;
  unit?: string;
  store: LrsSpecStore;
  options?: readonly { id: string; label: string }[];
}

const SPEC_COMMON: LrsSpecField[] = [
  { key: 'type', label: 'Asset Type', kind: 'readonly', store: 'column' },
  { key: 'owner_name', label: 'Owner', kind: 'text', store: 'column' },
  { key: 'material', label: 'Material', kind: 'text', store: 'column' },
  { key: 'installation_date', label: 'Installation Date', kind: 'date', store: 'column' },
  { key: 'last_inspection_date', label: 'Inspection Date', kind: 'date', store: 'column' },
  { key: 'condition', label: 'Condition', kind: 'select', store: 'column', options: LRS_CONDITIONS },
  { key: 'lifecycle_status', label: 'Lifecycle status', kind: 'select', store: 'column', options: LRS_LIFECYCLE_STAGES },
  { key: 'side', label: 'Road side', kind: 'select', store: 'column', options: [
    { id: 'L', label: 'Left' },
    { id: 'R', label: 'Right' },
    { id: 'C', label: 'Center' },
  ] },
  { key: 'median_offset_m', label: 'Offset from Road Median', kind: 'number', unit: 'm', store: 'column' },
  { key: 'edge_offset_m', label: 'Offset from Road Edge', kind: 'number', unit: 'm', store: 'column' },
  { key: 'start_chainage', label: 'Start Chainage', kind: 'text', store: 'column' },
  { key: 'end_chainage', label: 'End Chainage', kind: 'text', store: 'column' },
];

const SPEC_UG_PIPE: LrsSpecField[] = [
  { key: 'diameter_mm', label: 'Diameter', kind: 'number', unit: 'mm', store: 'column' },
  { key: 'depth_m', label: 'Depth', kind: 'number', unit: 'm', store: 'column' },
  { key: 'pressure_rating', label: 'Pressure rating', kind: 'text', store: 'properties' },
  { key: 'protection_type', label: 'Protection type', kind: 'text', store: 'properties' },
];

const SPEC_FIBER: LrsSpecField[] = [
  { key: 'diameter_mm', label: 'Diameter', kind: 'number', unit: 'mm', store: 'column' },
  { key: 'depth_m', label: 'Burial depth', kind: 'number', unit: 'm', store: 'column' },
  { key: 'fiber_count', label: 'Fiber count', kind: 'number', store: 'properties' },
  { key: 'cable_type', label: 'Cable type', kind: 'text', store: 'properties' },
  { key: 'duct_type', label: 'Duct type', kind: 'text', store: 'properties' },
  { key: 'chamber_spacing_m', label: 'Chamber spacing', kind: 'number', unit: 'm', store: 'properties' },
];

const SPEC_ELECTRIC: LrsSpecField[] = [
  { key: 'voltage', label: 'Voltage', kind: 'text', store: 'properties' },
  { key: 'cable_type', label: 'Cable type', kind: 'text', store: 'properties' },
  { key: 'circuits', label: 'Number of circuits', kind: 'number', store: 'properties' },
  { key: 'depth_m', label: 'Burial depth', kind: 'number', unit: 'm', store: 'column' },
  { key: 'duct_type', label: 'Duct type', kind: 'text', store: 'properties' },
];

const SPEC_OIL_GAS: LrsSpecField[] = [
  { key: 'diameter_mm', label: 'Pipeline diameter', kind: 'number', unit: 'mm', store: 'column' },
  { key: 'pressure_rating', label: 'Pressure', kind: 'text', store: 'properties' },
  { key: 'product', label: 'Product', kind: 'text', store: 'properties' },
  { key: 'wall_thickness_mm', label: 'Wall thickness', kind: 'number', unit: 'mm', store: 'properties' },
  { key: 'depth_m', label: 'Depth', kind: 'number', unit: 'm', store: 'column' },
  { key: 'casing', label: 'Casing', kind: 'text', store: 'properties' },
  { key: 'cathodic_protection', label: 'Cathodic protection', kind: 'text', store: 'properties' },
];

const SPEC_OG: LrsSpecField[] = [
  { key: 'height_m', label: 'Height', kind: 'number', unit: 'm', store: 'properties' },
  { key: 'clearance_m', label: 'Clearance', kind: 'number', unit: 'm', store: 'properties' },
  { key: 'pole_type', label: 'Pole / tower type', kind: 'text', store: 'properties' },
  { key: 'voltage', label: 'Voltage', kind: 'text', store: 'properties' },
  { key: 'cable_type', label: 'Cable type', kind: 'text', store: 'properties' },
];

const SPEC_CROSSING: LrsSpecField[] = [
  { key: 'crossing_type', label: 'Crossing type', kind: 'text', store: 'properties' },
  { key: 'crossing_angle_deg', label: 'Crossing angle', kind: 'number', unit: '°', store: 'properties' },
  { key: 'depth_m', label: 'Depth below road', kind: 'number', unit: 'm', store: 'column' },
  { key: 'clearance_from_pavement_m', label: 'Clearance from pavement', kind: 'number', unit: 'm', store: 'properties' },
  { key: 'clearance_from_formation_m', label: 'Clearance from formation', kind: 'number', unit: 'm', store: 'properties' },
  { key: 'protection_arrangement', label: 'Protection arrangement', kind: 'text', store: 'properties' },
  { key: 'casing', label: 'Casing information', kind: 'text', store: 'properties' },
];

const SPEC_FURNITURE: LrsSpecField[] = [
  { key: 'height_m', label: 'Height', kind: 'number', unit: 'm', store: 'properties' },
  { key: 'barrier_type', label: 'Barrier / furniture type', kind: 'text', store: 'properties' },
];

const SPEC_STRUCTURE: LrsSpecField[] = [
  { key: 'span_m', label: 'Span', kind: 'number', unit: 'm', store: 'properties' },
  { key: 'clearance_m', label: 'Clearance', kind: 'number', unit: 'm', store: 'properties' },
  { key: 'structure_type', label: 'Structure type', kind: 'text', store: 'properties' },
];

const SPEC_ROAD: LrsSpecField[] = [
  { key: 'width_m', label: 'Width', kind: 'number', unit: 'm', store: 'properties' },
  { key: 'surface_type', label: 'Surface / material type', kind: 'text', store: 'properties' },
  { key: 'camber_value', label: 'Camber / crossfall', kind: 'number', unit: '%', store: 'properties' },
];

const SPEC_BY_TYPE: Record<string, LrsSpecField[]> = {
  water: SPEC_UG_PIPE,
  sewer: SPEC_UG_PIPE,
  storm_drain: SPEC_UG_PIPE,
  drainage: SPEC_UG_PIPE,
  oil_gas: SPEC_OIL_GAS,
  ofc: SPEC_FIBER,
  utility_duct: SPEC_FIBER,
  electric_ht: SPEC_ELECTRIC,
  electric_lt: SPEC_ELECTRIC,
  chamber: SPEC_UG_PIPE,
  manhole: SPEC_UG_PIPE,
  valve_chamber: SPEC_UG_PIPE,
  inspection_chamber: SPEC_UG_PIPE,
  electric_pole: SPEC_OG,
  electric_tower: SPEC_OG,
  telecom_tower: SPEC_OG,
  oh_water_tank: SPEC_OG,
  sign: SPEC_FURNITURE,
  sign_board: SPEC_FURNITURE,
  lighting: SPEC_FURNITURE,
  km_stone: SPEC_FURNITURE,
  chainage_marker: SPEC_FURNITURE,
  guard_rail: SPEC_FURNITURE,
  crash_barrier: SPEC_FURNITURE,
  delineator: SPEC_FURNITURE,
  road_stud: SPEC_FURNITURE,
  gantry: SPEC_FURNITURE,
  bus_shelter: SPEC_FURNITURE,
  bollard: SPEC_FURNITURE,
  culvert: SPEC_STRUCTURE,
  box_culvert: SPEC_STRUCTURE,
  bridge: SPEC_STRUCTURE,
  rob: SPEC_STRUCTURE,
  rub: SPEC_STRUCTURE,
  flyover: SPEC_STRUCTURE,
  viaduct: SPEC_STRUCTURE,
  retaining_wall: SPEC_STRUCTURE,
  subway: SPEC_STRUCTURE,
  underpass: SPEC_STRUCTURE,
  overpass: SPEC_STRUCTURE,
  crossover: SPEC_CROSSING,
  pavement: SPEC_ROAD,
  shoulder: SPEC_ROAD,
  median: SPEC_ROAD,
  camber: SPEC_ROAD,
};

function mergeSpecFields(...groups: LrsSpecField[][]): LrsSpecField[] {
  const seen = new Set<string>();
  const out: LrsSpecField[] = [];
  for (const group of groups) {
    for (const field of group) {
      if (seen.has(field.key)) continue;
      seen.add(field.key);
      out.push(field);
    }
  }
  return out;
}

export function specFieldsForAsset(opts: {
  kind: 'event' | 'utility';
  typeId: string;
  grade?: LrsGrade | null;
}): LrsSpecField[] {
  const typeFields = SPEC_BY_TYPE[opts.typeId] || [];
  const grade = opts.grade;
  const extras: LrsSpecField[] = [];
  if (grade === 'crossover') extras.push(...SPEC_CROSSING);
  else if (grade === 'overground' && opts.kind === 'utility') extras.push(...SPEC_OG);
  else if (grade === 'underground' && opts.kind === 'utility' && !typeFields.length) extras.push(...SPEC_UG_PIPE);
  return mergeSpecFields(SPEC_COMMON, typeFields, extras);
}
